<template>
  <div>
    <div v-if="isEmptySlotList">
      <template v-if="isExpressAppointment">
        <AppointmentButtonCallLpu
          v-if="isMobile"
          :phone="lpuPhoneValue"
          :lpu-working-text="lpuWorkingText"
        />
      </template>
      <CalendarStub v-else />
    </div>
    <div
      v-else
      class="appointment-calendar"
    >
      <SliderGroup
        :items="dayItems"
        :active-index="activeIndex"
        :padding-x="paddingX"
        :padding-left="paddingLeft"
        :padding-right="paddingRight"
        :fog="fog"
        :scroll-to-active="!!initDay"
        @slider-group:click-item="handleClickItem($event)"
      >
        <template #default="{ item, index }">
          <SelectableTab
            :title="t(item.title, options)"
            :subtitle="t(item.subtitle, options)"
            :label="t(item.label, options)"
            :active="index === activeIndex"
            :disabled="item.disabled"
            small
          >
            <template
              v-if="(item.subtitle === 'Сб' || item.subtitle === 'Вс') && !item.disabled && index !== activeIndex"
              #subtitle
            >
              <span class="text-error--text">{{ t(item.subtitle, options) }}</span>
            </template>
          </SelectableTab>
        </template>
      </SliderGroup>
      <div
        class="mt-4"
        :style="{
          'padding-left': `${paddingLeft ?? paddingX}px`,
          'padding-right': `${paddingRight ?? paddingX}px`,
        }"
      >
        <AppointmentCalendarSlotButtons
          :is-disabled="isExpressAppointment"
          :doctor-id="doctorId"
          :current-day-slots="slots[activeDayItemDate]"
          :selected-slot="selectedSlot"
          :is-current-day-selected="selectedDay === activeDayItemDate"
          @slot-buttons:click="handleClickOnTime"
        />
        <template v-if="isExpressAppointment">
          <AppointmentButtonCallLpu
            v-if="isMobile"
            class="mt-4"
            :phone="lpuPhoneValue"
            :lpu-working-text="lpuWorkingText"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import I18NextVue from 'i18next'
import { computed, ref } from 'vue'
import { format, isValid, parseISO } from 'date-fns'
import { getDateFnsLocaleByIso } from 'www/utils'
import { APPOINTMENT_TYPES } from 'www/constants'
import { APPOINTMENT_YANDEX_GOALS, CLUB_YANDEX_GOALS } from 'www/yandexGoals'
import type { SlotGeneral, SlotsGeneral } from 'www/api/types'
import { AppointmentModules } from 'www/modules/appointment-modules'
import YaGoals from 'www/modules/YandexGoals'
import { getVisitType } from 'www/modules/Schedule/functions'
import { emitOpenGettingCard, isEnabledClubServicesSwitch } from 'www/global.js.blocks/common/club-card-getting/functions'
import useInvasiveServiceInfoStore from 'src/components/common/InvasiveServiceInfo/stores/useInvasiveServiceInfoStore'
import getDateItemsFromSlots from 'www/vue_components/common/AppointmentCalendar/functions/getDateItemsFromSlots'
import SelectableTab from 'www/vue_components/common/SelectableTab/SelectableTab.vue'
import SliderGroup from 'www/vue_components/common/SliderGroup/SliderGroup.vue'
import CalendarStub from 'www/vue_components/common/DoctorPageLpuList/components/DoctorAppointmentCard/components/WorkplaceOrServiceCalendar/components/CalendarStub.vue'
import AppointmentCalendarSlotButtons from 'www/vue_components/common/AppointmentCalendar/components/AppointmentCalendarSlotButtons.vue'
import AppointmentButtonCallLpu from 'www/vue_components/common/AppointmentButtonCallLpu/AppointmentButtonCallLpu.vue'

/* eslint-disable vue/require-default-prop */
const props = withDefaults(defineProps<{
  slots: SlotsGeneral
  paddingX?: number
  paddingLeft?: number
  paddingRight?: number
  fog?: boolean | 'left' | 'right'
  doctorId?: number
  lpuId?: number
  selectedDate?: SlotGeneral
  serviceData?: {
    allowTreatmentReferral: boolean
    requiresPrimaryConsultation: boolean
    hasPrimaryConsultationDoctors: boolean
    doctorSpecialities?: Array<{
      name: string
      translit: string
    }>
    townTranslit?: string
  }
  initDay?: string
  useRedirect?: boolean
  dataItem?: string
  doctorsLpuHref?: string
  isAppointmentDoctor?: boolean
  isAppointmentLpu?: boolean
  doctorSpec?: number | string
  specPlacement?: 0 | 1
  synonymId?: number
  dtypeId?: number
  isEnabledLpuClub?: boolean
  isLpuPriceList?: boolean
  isDoctorPriceList?: boolean
  serviceIsDirect?: boolean
  serviceIsExpress?: boolean
  appointmentType?: 'express' | 'direct' | ''
  lpuWorkingText?: string
  lpuPhone?: string | number
}>(), {
  paddingX: 16,
  fog: false,
  appointmentType: '',
  lpuPhone: '',
})

const isMobile = window.MOBILE_VERSION

const lpuPhoneValue = computed(() => (
  props.lpuPhone == null || props.lpuPhone === ''
    ? ''
    : String(props.lpuPhone)
))

const { t, language } = I18NextVue
const options = { ns: language }

const emit = defineEmits(['calendar:time-choice'])

const { redirectToNewAppointment, redirectToNewAppointmentClinic } = new AppointmentModules()
const yaGoalsInstance = new YaGoals()

const selectedDay = ref(props.initDay)

const dayItems = computed(() => {
  const locale = getDateFnsLocaleByIso(window.LANGUAGE_DATA.currentLanguage.iso)

  return getDateItemsFromSlots(props.slots, locale)
})
const activeIndex = ref(
  dayItems.value.findIndex(item => (!item.disabled && (!props.initDay || item.date === props.initDay))),
)
const activeDayItem = computed(() => dayItems.value[activeIndex.value] || dayItems.value[0])
const activeDayItemDate = computed(() => activeDayItem.value.date)

const isEmptySlotList = computed(() => !Object.keys(props.slots).some(date => props.slots[date].length))

const selectedSlot = ref(props.selectedDate)

const dateName = computed(() => {
  const selectedDayISO = parseISO(activeDayItemDate.value)

  if (!isValid(selectedDayISO)) {
    return ''
  }

  const locale = getDateFnsLocaleByIso(language)
  const day = format(selectedDayISO, 'cccccc', { locale })
  const formattedDay = day[0].toLowerCase() + day.slice(1)
  const formattedDayAndMonth = format(selectedDayISO, 'd MMMM', { locale })

  return `${formattedDayAndMonth} (${formattedDay})` // 6 июня (пн)
})

const isExpressDoctor = computed(() => props.appointmentType === 'express' && props.isAppointmentDoctor)

const isExpressServices = computed(() => (
  (
    props.isAppointmentLpu
    && !props.serviceIsDirect
    && props.serviceIsExpress
  ) || (
    props.isAppointmentLpu
    && props.serviceIsDirect
    && isEmptySlotList.value
    && props.serviceIsExpress
  )
))

const isExpressAppointment = computed(() => {
  const isExpress = isExpressDoctor.value

  return isExpress || isExpressServices.value
})

function redirectToAppointment(payload: {
  target: HTMLElement
  slotInGetOptions: string
  dataItem?: string
  slotDoctorId?: number
  additionalData?: {
    byReferral: 'true'
  }
}) {
  const {
    target,
    dataItem,
    slotDoctorId,
    slotInGetOptions,
  } = payload

  if (props.isAppointmentDoctor) {
    let visitType: string | null = ''
    let doctorSpec: string | null | undefined = ''
    let doctorSpecLpuInnerPageId: string | null | undefined = ''
    const parentContainer = target?.closest('.b-doctor-card__lpus-info') || target?.closest('.b-doctor-contacts__wp-info')
    const clubSwitchElement = parentContainer?.querySelector?.('[data-club-switch-block]')

    if (dataItem) {
      visitType = clubSwitchElement
        ? clubSwitchElement.getAttribute('data-club-switch-block')
        : getVisitType({ target, dataItem })

      try {
        doctorSpec = target.closest(`[${dataItem}]`) // если пользователь находится в мобильной версии на странице врача - он может выбрать специальность в 'select'
          ?.querySelector('[data-selected-speciality-id]')
          ?.getAttribute('data-selected-speciality-id') // ToDo schedule-important избавиться от привязки к DOM дереву

        const { hash } = window.location

        // Ветка для страницы списка врачей клиники. Если пользователь отфильтровал по специальности - достаем ее из хеша
        if (hash.includes('speciality')) {
          const [, doctorSpecId] = hash.split('=')

          if (doctorSpecId) { // eslint-disable-line max-depth
            doctorSpecLpuInnerPageId = doctorSpecId
          }
        }
      } catch { /* Empty */ }
    }

    if (visitType === APPOINTMENT_TYPES.club) {
      const { clickClubSlot } = CLUB_YANDEX_GOALS

      yaGoalsInstance.send({ name: clickClubSlot })
    }

    // @ts-ignore
    redirectToNewAppointment({
      lpu: props.lpuId,
      slot: slotInGetOptions,
      type: visitType,
      doctor: props.doctorId || slotDoctorId, // this.doctorId - для страницы врача/списка врачей, slotDoctorId - для страницы клиники/списка клиник
      doctorSpec: doctorSpecLpuInnerPageId || props.doctorSpec || doctorSpec, // doctorSpecLpuInnerPageId для списка врачей клиники, this.doctorSpec для страницы списка врачей, doctorSpec для страницы врача
      spec_placement: props.specPlacement,
    })

    return
  }

  if (props.isAppointmentLpu) {
    const redirectData: {
      slot: string
      lpu?: number
      synonymId?: number
      dTypeId?: number
      doctorId?: number
      equipmentId?: number | ''
      byReferral?: 'true'
    } = {
      slot: slotInGetOptions,
      lpu: props.lpuId,
      synonymId: props.synonymId,
    }

    if (payload.additionalData?.byReferral) {
      redirectData.byReferral = payload.additionalData.byReferral
    }

    if (dataItem) { // Поле dtype необязательно должно использоваться для редиректа на форму записи. Backend имеет механизм, с помощью которого его достает у себя(например он используется при переходе со страницы цен клинки)
      const selectedSynonymTypeId = target.closest(`[${dataItem}]`)
        ?.querySelector('[data-synonym-type-id]')
        ?.getAttribute('data-synonym-type-id') // ToDo schedule-important избавиться от привязки к DOM дереву

      redirectData.dTypeId = Number(selectedSynonymTypeId) || props.dtypeId // Если пользователь выбрал конкретный синоним, значит у синонима есть его 'dtypeId'. Если он неизвестен - берется 'dtypeId' со страницы.
    }

    /**
     * Поскольку перед редиректом на форму записи
     * если пользователь неизвестен и включен клубный switch
     * или если пользователь известен, но не имеет клубной карты
     * сначала показываем уведомление о покупке клубной карты.
     * Поэтому останавливаем редирект и делегируем переход в форму компоненту.
     */
    if (isEnabledClubServicesSwitch() && props.isEnabledLpuClub) {
      emitOpenGettingCard({ appointmentData: redirectData })
      return
    }

    const {
      clickServiceListSlotDirect,
      clickSlotServicePriceListDirect,
    } = APPOINTMENT_YANDEX_GOALS

    if (props.serviceIsDirect) {
      yaGoalsInstance.send({
        name: clickServiceListSlotDirect,
      })
    }

    if (props.isLpuPriceList && props.serviceIsDirect) {
      yaGoalsInstance.send({
        name: clickSlotServicePriceListDirect,
      })
    }

    if (props.isDoctorPriceList) {
      redirectData.doctorId = props.doctorId
      redirectData.equipmentId = ''

      yaGoalsInstance.send({
        name: 'doctor-price-list-click-slot',
      })
    }

    redirectToNewAppointmentClinic(redirectData)
  }
}

function onClickTime(event: Event, slot: SlotGeneral, additionalData?: { byReferral: 'true' }) {
  selectedSlot.value = slot
  selectedDay.value = activeDayItemDate.value

  const locale = getDateFnsLocaleByIso(language)

  if (props.useRedirect) {
    redirectToAppointment({
      target: event.target as HTMLElement,
      dataItem: props.dataItem,
      slotDoctorId: 'doctor_id' in slot ? slot.doctor_id : undefined,
      slotInGetOptions: `${format(parseISO(activeDayItemDate.value), 'dd.MM.yyyy', { locale })} ${slot.time}`,
      additionalData,
    })

    return
  }

  const dayFormatDot = format(parseISO(activeDayItemDate.value), 'dd.MM.yyyy', { locale })
  const dayFormatDash = format(parseISO(activeDayItemDate.value), 'yyyy-MM-dd', { locale })

  emit('calendar:time-choice', {
    day: dayFormatDash,
    time: slot.time,
    duration: slot.duration,
    dateName: dateName.value,
    selectedFullDate: `${dayFormatDot} ${slot.time}`,
  })
}

function handleClickOnTime(event: Event, slot: SlotGeneral) {
  if (props.serviceData) {
    const invasiveServiceInfoStore = useInvasiveServiceInfoStore()

    if (
      props.serviceData.allowTreatmentReferral
      || (props.serviceData.requiresPrimaryConsultation && props.serviceData.hasPrimaryConsultationDoctors)
    ) {
      invasiveServiceInfoStore.openInfoModal({
        allowTreatmentReferral: props.serviceData.allowTreatmentReferral,
        requiresPrimaryConsultation: props.serviceData.requiresPrimaryConsultation,
        hasPrimaryConsultationDoctors: props.serviceData.hasPrimaryConsultationDoctors,
        doctorSpecialities: props.serviceData.doctorSpecialities,
        townTranslit: props.serviceData.townTranslit,
        date: activeDayItemDate.value,
        handleConfirmReferral: () => {
          yaGoalsInstance.send({ name: 'appointment__serviceClickConfirmReferral' })
          onClickTime(event, slot, { byReferral: 'true' })
        },
        handleAppointment: () => {
          yaGoalsInstance.send({ name: 'appointment__serviceClickConfirmPreliminaryConsultation' })
          onClickTime(event, slot)
        },
      })

      return
    }
  }

  onClickTime(event, slot)
}

function handleClickItem(index: number) {
  if (!dayItems.value[index].disabled) {
    activeIndex.value = index
  }
}
</script>

<style scoped lang="scss">
@import '~www/themes/doctors/common/variables';

.appointment-calendar {
  max-width: 100%;
}
</style>
